import {
    eraseCookieByKey,
    getCookie,
    setCookie
} from '@libs/cookies';
import {
    AUTH_TOKEN_KEY,
    AUTH_TOKEN_TIME,
    REFRESH_TOKEN_KEY
} from 'auth/common';
import { apiClient } from '@libs/apiRequest';
import { store } from 'app/ReduxGate';
import { userEmailSelector } from 'auth/redux.selectors';
import {
    parseTokenExpireFullTime,
    parseTokenExpireTime
} from '@libs/parseJWT';

/**
 * Makes a fetch request with authentication token
 * @param {string} url - The API endpoint URL
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE, etc.)
 * @param {object} payload - Request body/params
 * @returns {Promise} - Response data
 */
export const fetchRequest = async (url, method = 'GET', payload = null) => {
    try {
        const refreshToken = getCookie(REFRESH_TOKEN_KEY);
        const authTokenRemaining = getCookie(AUTH_TOKEN_TIME);

        if (authTokenRemaining) {
            const remainingTime =
                JSON.parse(authTokenRemaining).exp - new Date().getTime();
            if (remainingTime < 0) {
                eraseCookieByKey(AUTH_TOKEN_KEY);
            }
        }

        const authToken = getCookie(AUTH_TOKEN_KEY);

        if (authToken) {
            apiClient.defaults.headers.common.Authorization = `Bearer ${authToken}`;
        } else if (refreshToken) {
            const email = userEmailSelector(store.getState());
            apiClient.defaults.headers.common.Authorization = `Bearer ${refreshToken}`;

            const { data } = await apiClient.post('auth/refresh', { email });

            setCookie(
                AUTH_TOKEN_KEY,
                data.authToken,
                parseTokenExpireTime(data.authToken)
            );
            setCookie(
                REFRESH_TOKEN_KEY,
                data.refreshToken,
                parseTokenExpireTime(data.refreshToken)
            );
            setCookie(
                AUTH_TOKEN_TIME,
                JSON.stringify({ exp: parseTokenExpireFullTime(data.authToken) })
            );

            apiClient.defaults.headers.common.Authorization = `Bearer ${data.authToken}`;
        }

        const config = {
            url,
            method: method.toLowerCase(),
            headers: {
                'Content-Type': 'application/json'
            }
        };

        // Add body for POST, PUT, PATCH requests
        if (payload && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
            config.data = payload;
        }

        // Add query params for GET requests
        if (payload && method.toUpperCase() === 'GET') {
            // Filter out undefined, null or empty string values so they don't
            // become 'key=undefined' in the query string
            const filtered = Object.keys(payload).reduce((acc, key) => {
                const val = payload[key];
                if (val !== undefined && val !== null && val !== '') {
                    acc[key] = val;
                }
                return acc;
            }, {});

            config.params = filtered;
        }

        const response = await apiClient(config);
        return response.data;
    } catch (error) {
        console.error('Fetch request error:', error);
        throw error;
    }
};

export default fetchRequest;